// Resolves a centre's DICOMweb RS base (<proxy_url>/rs) from its centre code,
// so a viewer opened for a study routes to *that study's* centre cache proxy
// rather than the logged-in user's primary centre.
//
// A proxy serves the study if it holds it, otherwise forwards to central, which
// federates across every centre's proxy + its own store. So any proxy is always
// correct — routing by the order's centre is purely a cache-locality
// optimization (a fast local hit for a recent study at its own centre). When the
// centre is unknown or has no proxy, callers fall back to the primary
// dicom_proxy, which is equally correct.

import { makeGetCall } from './helper';

let centreProxyMap: Record<string, string> | null = null;
let inflight: Promise<void> | null = null;

const norm = (v?: string) => (v || '').trim().toLowerCase();

// Mirror the backend's login-time construction: dicomProxy = proxy_url + '/rs'.
const toRsBase = (proxyUrl?: string): string | null =>
  proxyUrl ? `${proxyUrl.replace(/\/+$/, '')}/rs` : null;

/**
 * Fetch /centres once and cache code -> proxy_url. Idempotent and popup-safe to
 * fire-and-forget: on failure the cache stays unset so a later call retries, and
 * viewer opens fall back to the primary proxy meanwhile.
 */
export const primeCentreProxies = (): Promise<void> => {
  if (centreProxyMap) return Promise.resolve();
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await makeGetCall('/centres');
      const centres = res?.data?.data || [];
      const map: Record<string, string> = {};
      for (const c of centres) {
        if (c?.code && c?.proxy_url) map[norm(c.code)] = c.proxy_url;
      }
      centreProxyMap = map;
    } catch (e) {
      console.error('Failed to load centre proxies:', e);
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

/**
 * Synchronous lookup from the cached map. Returns <proxy_url>/rs for the centre,
 * or null when the map isn't loaded yet or the centre has no proxy configured.
 */
export const getCentreProxyRs = (centerCode?: string): string | null => {
  if (!centreProxyMap || !centerCode) return null;
  return toRsBase(centreProxyMap[norm(centerCode)]);
};
